/**
 * Estimation Survoltage — écran repris de la version du client (itérations 25, 26, 27, 32).
 *
 * Contenu, libellés et disposition sont les siens, bouton de synchronisation compris.
 * Les adaptations sont regroupées en haut du fichier, dans quatre fonctions nommées.
 *
 * Il ne reste qu'une différence de modèle, et elle ne retire rien à l'écran : les
 * MONTANTS du barème restent contractuels et non modifiables, car ils viennent du
 * document de juillet 2026. Les EFFECTIFS, eux, se saisissent et se recopient depuis
 * le parc comme chez lui — arbitrage du 10/08.
 *
 * Une lecture à ne pas confondre : le barème rémunère un client selon son profil, pas
 * un équipement. Le revenu par équipement du tableau est un prorata, et son sous-titre
 * le dit.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/shared/auth/AuthContext';
import useCapacites from '@/shared/auth/useCapacites';
import { CAP } from '@/shared/auth/capacites';
import { Navigate } from 'react-router-dom';
import api from '@/shared/api/client';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Card, CardContent } from '@/shared/ui/card';
import { Slider } from '@/shared/ui/slider';
import { Switch } from '@/shared/ui/switch';
import { Separator } from '@/shared/ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/shared/ui/table';
import { Badge } from '@/shared/ui/badge';
import Breadcrumb from '@/shared/components/Breadcrumb';
import AggregatorDocuments from '@/shared/components/AggregatorDocuments';
import AggregatorAssetsTable from '@/shared/components/AggregatorAssetsTable';
import { SurvoltageRevenueWidget } from '@/shared/components/AggregatorRevenueWidgets';
import MentionEstimation from '@/shared/components/MentionEstimation';
import { toast } from 'sonner';
import {
  Save, Plus, Trash2, TrendingUp, TrendingDown, Euro, Sparkles,
  ChevronDown, ChevronRight, RefreshCw, Info, AlertCircle,
  Building, ListChecks, Calculator, ScaleIcon,
} from 'lucide-react';

const PAGE_AGGREGATEUR = 'Survoltage';

const MECHANISMS = [
  { key: 'mecapa', label: 'MECAPA', family: 'capacitaire' },
  { key: 'nebco', label: 'NEBCO', family: 'energie' },
  { key: 'ma_capa', label: 'MA Capa', family: 'equilibrage' },
  { key: 'ma_energie', label: 'MA Énergie', family: 'equilibrage' },
  { key: 'r2_capa', label: 'R2 Capa', family: 'equilibrage' },
  { key: 'r2_energie', label: 'R2 Énergie', family: 'equilibrage' },
];

const fmtEUR = (v) => (v == null ? '—' : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 })} €`);
const fmtEUR2 = (v) => (v == null ? '—' : `${Number(v).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`);
const fmtDate = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return iso; }
};

// --- Adaptation de notre API vers la forme attendue par ses composants -----------
//
// Trois conversions, pour trois différences de modèle assumées.
//
// 1. Le barème est CONTRACTUEL chez nous : il vient du document de juillet 2026 et
//    n'est pas modifiable à l'écran. Sa version le range dans la configuration et le
//    rend éditable. On expose la même forme, en lecture seule.
// 2. Le nombre de clients par profil vient du PARC RÉEL, en permanence. Sa version le
//    fait saisir à la main, d'où son bouton « Utiliser les valeurs du parc ». Chez
//    nous ce bouton n'aurait rien à faire : les comptes SONT le parc.
// 3. Le barème rémunère un CLIENT selon son profil, jamais un équipement. Le revenu
//    par équipement affiché dans le tableau est un prorata, et le sous-titre du
//    tableau le dit.

const adapterActifs = (charge) => {
  const ligne = (a, statut) => ({
    ...a,
    id: a.asset_id,
    aggregateur: 'Survoltage',
    statut,
    revenu: a.annuel ?? null,
    raison_exclusion: a.motif_libelle || null,
  });
  return [
    ...(charge?.retenus || []).map((a) => ligne(a, 'actif')),
    ...(charge?.ecartes || []).map((a) =>
      ligne(a, a.motif === 'exclu_manuellement' ? 'exclu' : 'en_attente')),
  ];
};

const adapterParc = (pa) => {
  // Notre route sert désormais les mêmes champs que la sienne : `by_profile` avec
  // l'effectif saisi et l'effectif du parc côte à côte, plus les totaux. On transmet.
  if (!pa) return null;
  return pa;
};

const adapterResume = (s) => {
  // Idem : `par_profil` porte déjà `count`, `unit_low/high` et `total_low/median/high`,
  // calculés par le serveur à partir des effectifs saisis. On renomme, on ne recalcule
  // pas : le serveur les sert, deux calculs concurrents finiraient par diverger.
  if (!s) return null;
  return {
    ...s,
    by_profile: s.par_profil || {},
    by_family: s.par_famille || {},
    totals: {
      clients: s.totaux?.clients,
      monthly_median: s.totaux?.mensuel_median,
      annual_median: s.totaux?.annuel_median,
      annual_low: s.totaux?.annuel_bas,
      annual_high: s.totaux?.annuel_haut,
    },
  };
};

// La configuration porte l'effectif saisi de chaque profil, c'est elle qui fait foi.
const adapterConfig = (cfg) => cfg;

export default function SurvoltagePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // La garde lit la CAPACITE, plus une liste de roles ecrite ici (lot I). Cette liste
  // etait une copie de la matrice du serveur : le 30/08, le gestionnaire a perdu
  // `tarifs:read` et la copie ne l'a pas su -- l'ecran s'ouvrait encore et restait vide.
  const { possede, pret } = useCapacites();
  const hasAccess = !pret || possede(CAP.TARIFS_READ);

  const [cfg, setCfg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [parkAnalysis, setParkAnalysis] = useState(null);
  const [versements, setVersements] = useState([]);
  const [assetsList, setAssetsList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [savingVersement, setSavingVersement] = useState(false);

  const [profileFilter, setProfileFilter] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [expandedProfile, setExpandedProfile] = useState(null);

  const [versementForm, setVersementForm] = useState({
    periode: '', date_versement: new Date().toISOString().slice(0, 10), montant_reel: '',
  });

  const load = useCallback(async () => {
    try {
      const [c, s, pa, v, ass] = await Promise.all([
        api.get('/survoltage/config'),
        api.get('/survoltage/revenue-summary'),
        api.get('/survoltage/park-analysis'),
        api.get('/survoltage/versements'),
        api.get('/survoltage/assets'),
      ]);
      const parc = adapterParc(pa.data);
      const resume = adapterResume(s.data);
      setCfg(adapterConfig(c.data));
      setSummary(resume);
      setParkAnalysis(parc);
      // `/versements` rend `{versements, total_recu, total_attendu}` chez nous, un
      // tableau chez lui. Sans cette extraction, `versements.map` lève et l'écran
      // entier reste blanc : c'est la panne du 10/08, invisible à toute vérification
      // qui ne charge pas la page.
      setVersements(v.data?.versements || []);
      setAssetsList(adapterActifs(ass.data));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur de chargement');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!user) return;
    if (!hasAccess) { setLoading(false); return; }
    load();
  }, [user, hasAccess, load]);

  if (user && !hasAccess) {
    const roleHome = user.role === 'installateur' ? '/installateur'
      : user.role === 'distributeur' ? '/sav' : '/dashboard';
    return <Navigate to={roleHome} replace />;
  }

  if (loading || !cfg || !summary) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-6 h-6 border-2 border-neutral-300 border-t-neutral-900 rounded-full animate-spin" />
      </div>
    );
  }

  const totals = summary.totals || {};
  const byProfile = summary.by_profile || {};
  // Les profils ou l effectif SAISI ne dit pas la meme chose que le parc OBSERVE.
  // C est la seule facon de lire un total sans se tromper sur sa nature : l ecran
  // Survoltage projette une exploitation, il ne constate pas un parc. Le tableau de
  // bord, lui, constate — c est la correction F4/F6 posee cote serveur.
  const ecartsProfils = Object.entries(parkAnalysis?.by_profile || {})
    .map(([cle, p]) => ({ label: p.label || cle, saisi: p.count_saisi ?? 0, parc: p.count_parc ?? 0 }))
    .filter(e => e.saisi !== e.parc);
  const byFamily = summary.by_family || {};
  const untracked = parkAnalysis?.untracked_profiles || {};

  const updateCount = (pkey, val) => {
    setCfg((c) => ({ ...c, profiles: { ...c.profiles, [pkey]: { ...c.profiles[pkey], count: Math.max(0, Number(val) || 0) } } }));
  };
  const updateMech = (pkey, mkey, bound, val) => {
    setCfg((c) => ({ ...c, profiles: { ...c.profiles, [pkey]: { ...c.profiles[pkey], [mkey]: { ...c.profiles[pkey][mkey], [bound]: Math.max(0, Number(val) || 0) } } } }));
  };

  const save = async () => {
    if (!isAdmin) return;
    setSaving(true);
    try {
      await api.put('/survoltage/config', {
        etat_parc: cfg.etat_parc,
        part_operateur: cfg.part_operateur,
        projection_3ans: cfg.projection_3ans,
        profiles: Object.fromEntries(Object.entries(cfg.profiles).map(([k, p]) => [k, {
          count: p.count,
          ...Object.fromEntries(MECHANISMS.map(({ key }) => [key, p[key]])),
        }])),
      });
      toast.success('Configuration Survoltage enregistrée');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSaving(false);
  };

  // Recopie les effectifs observés dans le parc vers les effectifs saisis. Le serveur
  // écrase, il ne fusionne pas : un exploitant qui clique veut aligner sa
  // configuration sur la réalité, pas obtenir un mélange dont il ne saurait plus ce
  // qu'il contient.
  const applyParkCounts = async () => {
    if (!isAdmin) return;
    setApplying(true);
    try {
      await api.post('/survoltage/apply-park-counts');
      toast.success(`Effectifs synchronisés (${parkAnalysis?.total_clients_survoltage || 0} clients)`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setApplying(false);
  };

  const addVersement = async (e) => {
    e.preventDefault();
    if (!versementForm.montant_reel || !versementForm.date_versement) {
      toast.error('Montant et date requis');
      return;
    }
    setSavingVersement(true);
    try {
      await api.post('/survoltage/versements', {
        periode: versementForm.periode || 'Sans période',
        date_versement: versementForm.date_versement,
        montant_reel: parseFloat(versementForm.montant_reel),
        estime_a_date: (totals.monthly_median || 0),
      });
      toast.success('Versement ajouté');
      setVersementForm({ periode: '', date_versement: new Date().toISOString().slice(0, 10), montant_reel: '' });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    }
    setSavingVersement(false);
  };

  const deleteVersement = async (id) => {
    if (!window.confirm('Supprimer ce versement ?')) return;
    try {
      await api.delete(`/survoltage/versements/${id}`);
      toast.success('Versement supprimé');
      await load();
    } catch { toast.error('Erreur'); }
  };

  // Profils actifs (count > 0) pour la ventilation
  const activeProfiles = Object.entries(cfg.profiles)
    .map(([pkey, p]) => ({ pkey, ...p, node: byProfile[pkey] || {} }))
    .filter(p => (p.count || 0) > 0);

  return (
    <div className="p-6 md:p-8 lg:p-10 space-y-6" data-testid="survoltage-page">
      {/* Header */}
      <div>
        <Breadcrumb items={[{ label: 'Agrégateurs', to: '/agregateurs' }, { label: 'Estimation Survoltage' }]} />
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900">Estimation Survoltage</h1>
          <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 text-[10px]">Opérateur / Admin</Badge>
          {cfg.projection_3ans?.enabled && (
            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">Projection 3 ans</Badge>
          )}
        </div>
        <p className="text-sm text-neutral-400 mt-0.5">
          {parkAnalysis?.total_clients_survoltage ?? 0} clients Survoltage dans le parc —
          <span className="font-medium text-violet-700 ml-1">{totals.clients ?? 0} clients répartis en {activeProfiles.length} profil{activeProfiles.length > 1 ? 's' : ''} actif{activeProfiles.length > 1 ? 's' : ''}</span>
        </p>
        {/* RETRAIT VOLONTAIRE DU 14/09/2026, NE PAS « REPARER » EN LE REMETTANT.
            Deux reperes contractuels sortaient ici : « Barème juillet 2026 », accroche a
            la phrase au-dessus, et ce bouton vers le PDF de valorisation. Demande de
            Boubacar : l'ecran d'estimation ne les affiche plus.

            CE QUI N'A PAS CHANGE, et c'est le piege : le barème RESTE contractuel. Le
            serveur sert toujours `reference_doc_url` et `reference_doc_label`
            (`services/survoltage.py`), `test_conformite_revenus.py` exige toujours qu'un
            document de reference existe, et les montants ne sont toujours pas modifiables
            a l'ecran. On a retire un AFFICHAGE, pas une regle : ne pas relire ce retrait
            comme « les montants seraient devenus estimatifs ».

            LA RESERVE, ELLE, RESTE : `MentionEstimation source="survoltage"` plus bas dit
            toujours sur quoi le calcul repose. Elle nomme encore le barème de juillet
            2026, et c'est volontaire — une reserve qui ne dit pas de quoi le chiffre
            depend ne dit rien. `test_estimation_survoltage_allegee.py` garde les deux
            bords : l'en-tete muet ET la reserve intacte.

            LE DIV EST DEVENU CONDITIONNEL parce qu'il ne porte plus qu'un bouton
            d'administrateur. Laisse inconditionnel, il rendait un conteneur vide avec sa
            marge `mt-2` pour tout operateur non-admin : un blanc de 8 px sans cause
            visible, la meme dette qu'un separateur orphelin. */}
        {isAdmin && (
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Button size="sm" onClick={save} disabled={saving} data-testid="survoltage-save-btn">
              <Save className="w-3.5 h-3.5 mr-1.5" />
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        )}
      </div>

      {ecartsProfils.length > 0 && (
        <div className="p-3 rounded-md bg-amber-50/70 border border-amber-200 text-[12px] text-amber-900 flex items-start gap-2" data-testid="ecart-saisi-parc">
          <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
          <div>
            <strong>Les montants ci-dessous ne décrivent pas le parc.</strong>{' '}
            Cet écran calcule sur des effectifs saisis à la main, et {ecartsProfils.length}{' '}
            profil{ecartsProfils.length > 1 ? 's diffèrent' : ' diffère'} de ce que le parc montre :{' '}
            {ecartsProfils.map(e => `${e.label} ${e.saisi} au lieu de ${e.parc}`).join(' · ')}.
            {isAdmin && ' Le bouton « Utiliser les valeurs du parc », plus bas, aligne les deux.'}
            {' '}Le tableau de bord, lui, affiche toujours le parc réel.
          </div>
        </div>
      )}

      {/* Widget Estimation Survoltage restauré depuis le Dashboard */}
      <SurvoltageRevenueWidget summary={summary} />

      {/* KPI totaux avec ventilation */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="survoltage-kpis">
        <KPICard label="Estimation mensuelle médiane" value={fmtEUR(totals.monthly_median)} accent="text-violet-700" icon={Euro} tid="kpi-mensuel" />
        <KPICard label="Estimation annuelle médiane" value={fmtEUR(totals.annual_median)} accent="text-neutral-900" icon={TrendingUp} tid="kpi-annuel" />
        <KPICard label="Fourchette basse estimée (annuel)" value={fmtEUR(totals.annual_low)} accent="text-emerald-700" icon={Calculator} tid="kpi-bas" />
        <KPICard label="Fourchette haute estimée (annuel)" value={fmtEUR(totals.annual_high)} accent="text-amber-700" icon={Sparkles} tid="kpi-haut" />
      </div>

      {/* Bandeau contexte : état parc + part opérateur */}
      <div className="rounded-lg border border-neutral-200/80 bg-white p-4 flex items-center gap-6 flex-wrap" data-testid="survoltage-context-banner">
        <div>
          <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">État du parc</p>
          <p className="text-lg font-semibold text-neutral-900" data-testid="ctx-etat-parc">{Math.round((cfg.etat_parc || 0) * 100)} %</p>
        </div>
        <Separator orientation="vertical" className="h-8" />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">Part opérateur</p>
          <p className="text-lg font-semibold text-emerald-700" data-testid="ctx-part-operateur">{Math.round((cfg.part_operateur ?? 1) * 100)} %</p>
        </div>
        <Separator orientation="vertical" className="h-8" />
        <div>
          <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">Capacitaire estimé (MECAPA)</p>
          <p className="text-sm font-medium text-neutral-800">{fmtEUR(byFamily.capacitaire)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">Énergie estimée (NEBCO)</p>
          <p className="text-sm font-medium text-neutral-800">{fmtEUR(byFamily.energie)}</p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">Équilibrage estimé (MA+R2)</p>
          <p className="text-sm font-medium text-neutral-800">{fmtEUR(byFamily.equilibrage)}</p>
        </div>
      </div>

      <MentionEstimation source="survoltage" bloc="survoltage-kpis" />

      {/* Ventilation par profil client — cartes cliquables */}
      <div>
        <div className="flex items-baseline justify-between mb-3">
          <h2 className="text-[13px] font-medium text-neutral-900">Ventilation estimée par profil client</h2>
          {profileFilter && (
            <button onClick={() => setProfileFilter(null)} className="text-[11px] text-violet-700 hover:text-violet-900 font-medium underline" data-testid="clear-profile-filter">
              Effacer le filtre ({cfg.profiles[profileFilter]?.label})
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {Object.entries(cfg.profiles).map(([pkey, p]) => {
            const node = byProfile[pkey] || {};
            const parkCount = parkAnalysis?.by_profile?.[pkey]?.count_parc ?? 0;
            const isActive = profileFilter === pkey;
            const disabled = (p.count || 0) === 0 && parkCount === 0;
            return (
              <button
                key={pkey}
                type="button"
                onClick={() => { if (disabled) return; setProfileFilter(prev => prev === pkey ? null : pkey); }}
                disabled={disabled}
                className={`text-left p-4 rounded-lg border bg-white transition-all ${
                  isActive
                    ? 'border-violet-500 ring-2 ring-violet-100 bg-violet-50/40 shadow-sm'
                    : disabled
                    ? 'border-neutral-200/80 opacity-60 cursor-not-allowed'
                    : 'border-neutral-200/80 hover:border-violet-300 hover:shadow-sm cursor-pointer'
                }`}
                data-testid={`survoltage-profile-card-${pkey}`}
                aria-pressed={isActive}
              >
                <div className="flex items-start gap-2 mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-neutral-900 truncate">{p.label}</p>
                    <p className="text-[10px] text-neutral-400">
                      {p.count || 0} saisi{(p.count || 0) > 1 ? 's' : ''} · {parkCount} parc réel
                    </p>
                  </div>
                  {isActive && (
                    <span className="text-[9px] font-semibold text-violet-700 bg-violet-100 px-1.5 py-0.5 rounded-full">Filtre actif</span>
                  )}
                </div>
                {(p.count || 0) === 0 ? (
                  <p className="text-[11px] text-neutral-400 italic">Aucun client saisi</p>
                ) : (
                  <div className="space-y-1 text-[11px]">
                    <Row l="Médian estimé /an" v={fmtEUR(node.total_median)} strong />
                    <Row l="Bas estimé" v={fmtEUR(node.total_low)} />
                    <Row l="Haut estimé" v={fmtEUR(node.total_high)} />
                    <Row l="Par client (estimé)" v={`${fmtEUR(node.unit_low)} – ${fmtEUR(node.unit_high)}`} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <MentionEstimation source="survoltage" bloc="survoltage-profils" className="mt-3" />
      </div>

      {/* ACTIFS PRIS EN COMPTE DANS LE CALCUL — bloc harmonise Symphonics */}
      <Card className="border-neutral-200/80" data-testid="survoltage-actifs-pris-en-compte">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
            <div>
              <p className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                <ListChecks className="w-4 h-4 text-emerald-600" />
                Actifs pris en compte dans le calcul
              </p>
              <p className="text-[11px] text-neutral-500 mt-0.5">
                Nombre d&apos;actifs actifs par type inclus dans l&apos;estimation + détail des exclusions.
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] uppercase tracking-wider text-neutral-400">Total inclus</p>
              <p className="text-2xl font-semibold text-emerald-700" data-testid="survoltage-total-inclus">
                {Object.values(summary.actifs_pris_en_compte || {}).reduce((a,b) => a + Number(b || 0), 0)}
              </p>
            </div>
          </div>
          {Object.keys(summary.actifs_pris_en_compte || {}).length === 0 ? (
            <p className="text-[12px] text-neutral-400 italic">Aucun actif Survoltage sous mandat actuellement.</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              {Object.entries(summary.actifs_pris_en_compte).map(([type, count]) => (
                <div key={type} className="p-3 rounded-md border border-emerald-100 bg-emerald-50/30" data-testid={`inclus-${type}`}>
                  <p className="text-[10px] uppercase tracking-wider text-emerald-700 font-medium">{type}</p>
                  <p className="text-xl font-semibold text-neutral-900 mt-0.5">{count}</p>
                  <p className="text-[10px] text-neutral-400">actif{count > 1 ? 's' : ''} inclus</p>
                </div>
              ))}
            </div>
          )}
          {/* Exclus */}
          {(summary.actifs_exclus?.length > 0 || (summary.actifs_en_attente_parametrage || 0) > 0) && (
            <div className="mt-4 pt-4 border-t border-neutral-100" data-testid="survoltage-actifs-exclus">
              <p className="text-[11.5px] font-semibold text-amber-700 mb-2 flex items-center gap-1.5">
                <AlertCircle className="w-3.5 h-3.5" /> Actifs exclus du calcul ({(summary.actifs_exclus?.length || 0) + (summary.actifs_en_attente_parametrage || 0)})
              </p>
              <div className="space-y-1.5">
                {(summary.actifs_en_attente_parametrage || 0) > 0 && (
                  <div className="text-[11.5px] p-2 rounded bg-amber-50/60 border border-amber-100" data-testid="exclus-en-attente">
                    <strong>{summary.actifs_en_attente_parametrage}</strong> clients en attente de paramétrage
                    <span className="text-neutral-500 ml-1">— profils sans mécanisme configuré (valeurs bas/haut à 0)</span>
                  </div>
                )}
                {(() => {
                  const byRaison = {};
                  (summary.actifs_exclus || []).forEach(a => {
                    const key = a.raison || 'autre';
                    byRaison[key] = (byRaison[key] || 0) + 1;
                  });
                  return Object.entries(byRaison).map(([raison, count]) => (
                    <div key={raison} className="text-[11.5px] p-2 rounded bg-neutral-50 border border-neutral-100" data-testid={`exclus-${raison}`}>
                      <strong>{count}</strong> actif{count > 1 ? 's' : ''}
                      <span className="text-neutral-500 ml-1">
                        {raison === 'deconnecte' && '— déconnecté(s) ou en erreur de communication'}
                        {raison === 'sans mandat' && '— sans mandat signé ou en attente de signature'}
                        {raison !== 'deconnecte' && raison !== 'sans mandat' && `— ${raison}`}
                      </span>
                    </div>
                  ));
                })()}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tableau standardisé "Actifs pris en compte dans le calcul" */}
      <AggregatorAssetsTable
        aggregator="Survoltage"
        assets={assetsList}
        accentColor="text-violet-700"
        badgeClass="bg-violet-600 hover:bg-violet-700 text-white"
        filterAggregator={true}
        isAdmin={isAdmin}
        supportsCustomization={false}
        onRefresh={load}
      />

      {/* Parc réel — synchronisation counts */}
      {parkAnalysis && (
        <Card className="border-neutral-200/80" data-testid="park-analysis-card">
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
              <div>
                <p className="text-sm font-semibold text-neutral-900 flex items-center gap-2">
                  <Building className="w-4 h-4" />
                  Parc réel Survoltage
                </p>
                <p className="text-[11.5px] text-neutral-500 mt-1">
                  <strong className="text-neutral-700">{parkAnalysis.total_clients_survoltage}</strong> clients affectés à Survoltage
                  {' · '}
                  <strong className="text-neutral-700">{parkAnalysis.total_assets_survoltage}</strong> actifs
                  {parkAnalysis.clients_sans_actif > 0 && (
                    <span className="text-amber-600"> · {parkAnalysis.clients_sans_actif} sans actif</span>
                  )}
                  {parkAnalysis.clients_non_classes > 0 && (
                    <span className="text-amber-600"> · {parkAnalysis.clients_non_classes} non classé{parkAnalysis.clients_non_classes > 1 ? 's' : ''}</span>
                  )}
                </p>
              </div>
              {isAdmin && parkAnalysis.total_clients_survoltage > 0 && (
                <Button variant="outline" size="sm" onClick={applyParkCounts} disabled={applying} data-testid="apply-park-counts-btn">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${applying ? 'animate-spin' : ''}`} />
                  Utiliser les valeurs du parc
                </Button>
              )}
            </div>
            {Object.keys(untracked).length > 0 && (
              <div className="p-2.5 rounded-md bg-amber-50/70 border border-amber-100 text-[11.5px] text-amber-800 flex items-start gap-2" data-testid="untracked-warning">
                <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <div>
                  <strong>Profils non trackables :</strong>{' '}
                  {Object.entries(untracked).map(([pk, msg]) => (
                    <span key={pk} className="mr-1">{cfg.profiles[pk]?.label || pk} ({msg}).</span>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Liste détaillée : profils saisis (filtrée) */}
      <div className="rounded-lg border border-neutral-200/80 bg-white overflow-hidden" data-testid="survoltage-profiles-table">
        <div className="px-5 py-3 border-b border-neutral-100 flex items-center justify-between">
          <div>
            <h2 className="text-[13px] font-medium text-neutral-900">Profils pris en compte dans le calcul (montants estimés)</h2>
            <p className="text-[11px] text-neutral-400 mt-0.5">
              Sous mandat <span className="font-medium text-violet-700">{PAGE_AGGREGATEUR}</span>
              {profileFilter && <> · filtré sur <span className="font-medium">{cfg.profiles[profileFilter]?.label}</span></>}
              {' — '}Ajustez le nombre de clients pour chaque profil ci-dessous
            </p>
          </div>
          <Badge variant="outline" className="text-[10px] bg-neutral-50" data-testid="profiles-counter">
            {activeProfiles.length} profil{activeProfiles.length > 1 ? 's' : ''}
          </Badge>
        </div>
        <div className="max-h-[480px] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 bg-white z-10">
              <TableRow>
                <TableHead className="w-[90px]">Agrégateur</TableHead>
                <TableHead>Profil client</TableHead>
                <TableHead className="w-[100px] text-right">Clients</TableHead>
                <TableHead className="w-[130px] text-right">Bas estimé /an</TableHead>
                <TableHead className="w-[130px] text-right">Médian estimé /an</TableHead>
                <TableHead className="w-[130px] text-right">Haut estimé /an</TableHead>
                <TableHead className="w-[110px]">Parc réel</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(() => {
                const rows = Object.entries(cfg.profiles)
                  .filter(([pkey, p]) => (!profileFilter || pkey === profileFilter) && ((p.count || 0) > 0 || (parkAnalysis?.by_profile?.[pkey]?.count_parc || 0) > 0));
                if (rows.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-[12px] text-neutral-400 py-6" data-testid="profiles-empty">
                        Aucun profil ne correspond{profileFilter ? ` au filtre "${cfg.profiles[profileFilter]?.label}"` : ''}.
                      </TableCell>
                    </TableRow>
                  );
                }
                return rows.map(([pkey, p]) => {
                  const node = byProfile[pkey] || {};
                  const parkCount = parkAnalysis?.by_profile?.[pkey]?.count_parc ?? 0;
                  const isUntracked = pkey in untracked;
                  return (
                    <TableRow key={pkey} className="bg-violet-50/20" data-testid={`survoltage-row-${pkey}`}>
                      <TableCell>
                        <Badge className="bg-violet-600 hover:bg-violet-700 text-white text-[10px]">{PAGE_AGGREGATEUR}</Badge>
                      </TableCell>
                      <TableCell className="text-[12px] text-neutral-800">
                        {p.label}
                        {isUntracked && (
                          <AlertCircle className="w-3 h-3 text-amber-500 inline-block ml-1" />
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Input
                          type="number"
                          min="0"
                          value={p.count}
                          onChange={(e) => updateCount(pkey, e.target.value)}
                          className="w-20 h-8 text-right text-[12px] ml-auto"
                          disabled={!isAdmin}
                          data-testid={`profile-count-${pkey}`}
                        />
                      </TableCell>
                      <TableCell className="text-right text-[12px] text-neutral-600">{fmtEUR(node.total_low)}</TableCell>
                      <TableCell className="text-right text-[12px] font-medium">{fmtEUR(node.total_median)}</TableCell>
                      <TableCell className="text-right text-[12px] text-neutral-600">{fmtEUR(node.total_high)}</TableCell>
                      <TableCell className="text-[12px] text-neutral-500">
                        <span className="font-semibold text-neutral-900">{parkCount}</span>
                        {parkCount > 0 && p.count !== parkCount && (
                          <span className="text-amber-600 text-[10px] ml-1">≠</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                });
              })()}
            </TableBody>
          </Table>
        </div>
        <MentionEstimation source="survoltage" bloc="survoltage-tableau"
                           className="px-4 pb-3" />
      </div>

      {/* Configuration Survoltage — état parc + part opérateur + projection */}
      <div className="rounded-lg border border-neutral-200/80 bg-white p-5 space-y-4" data-testid="survoltage-config">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-[13px] font-medium text-neutral-900">Paramètres de calcul Survoltage</h2>
            <p className="text-[11px] text-neutral-400">
              Formule : <span className="font-mono">Estimation = Σ (clients × moyenne_profil) × état_parc × part_opérateur</span>
            </p>
          </div>
          {!isAdmin && <Badge variant="outline" className="text-[10px]">Lecture seule</Badge>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-[12px] font-medium text-neutral-800">État du parc</p>
                <p className="text-[10.5px] text-neutral-500">Taux d&apos;équipements réellement pilotables.</p>
              </div>
              <span className="text-lg font-semibold text-neutral-900" data-testid="etat-parc-value">
                {Math.round((cfg.etat_parc || 0) * 100)} %
              </span>
            </div>
            <Slider
              min={0} max={100} step={1}
              value={[Math.round((cfg.etat_parc || 0) * 100)]}
              onValueChange={([v]) => setCfg((c) => ({ ...c, etat_parc: v / 100 }))}
              disabled={!isAdmin}
              data-testid="etat-parc-slider"
            />
          </div>
          <div>
            <div className="flex items-center justify-between mb-2">
              <div>
                <p className="text-[12px] font-medium text-neutral-800">Part opérateur</p>
                <p className="text-[10.5px] text-neutral-500">Part conservée après négociation Survoltage (100 % = brut).</p>
              </div>
              <span className="text-lg font-semibold text-emerald-700" data-testid="part-operateur-value">
                {Math.round((cfg.part_operateur ?? 1) * 100)} %
              </span>
            </div>
            <Slider
              min={0} max={100} step={1}
              value={[Math.round((cfg.part_operateur ?? 1) * 100)]}
              onValueChange={([v]) => setCfg((c) => ({ ...c, part_operateur: v / 100 }))}
              disabled={!isAdmin}
              data-testid="part-operateur-slider"
            />
          </div>
        </div>
        {/* F7 — « Projection 3 ans » retiree de l ecran le 27/08, a la demande de David.
            Elle appliquait -30 % sur le Mecanisme d Ajustement et une decote parametrable
            sur la Reserve Secondaire : un chiffre projete, que personne n avait valide,
            pose a cote de chiffres reels. Le calcul reste ENTIER au serveur
            (services/survoltage.revenu_profil, DECOTE_MA et DECOTE_R2_DEFAUT) : c est une
            commande qu on masque, pas une fonction qu on supprime, et l item dit
            « a reactiver plus tard ».

            Le bandeau ambre du bandeau de titre, lui, RESTE : si un sous-compte avait deja
            la projection active en base, retirer l ecran sans rien dire appliquerait une
            decote de 30 % que plus personne ne pourrait ni voir ni lever. */}
        {isAdmin && (
          <div className="flex items-center justify-end pt-2 border-t border-neutral-100">
            <Button size="sm" onClick={save} disabled={saving} data-testid="save-survoltage-config-btn">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Enregistrement…' : 'Enregistrer'}
            </Button>
          </div>
        )}
      </div>

      {/* Versements réels vs estimés */}
      <div className="rounded-lg border border-neutral-200/80 bg-white p-5 space-y-4" data-testid="survoltage-versements">
        <div>
          <h2 className="text-[13px] font-medium text-neutral-900">Versements réels Survoltage</h2>
          <p className="text-[11px] text-neutral-400">Comparaison estimé (au moment de la saisie) vs réel versé</p>
        </div>
        <form onSubmit={addVersement} className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end p-3 rounded-md bg-neutral-50/60 border border-neutral-100" data-testid="versement-form">
          <div>
            <Label className="text-[11px] text-neutral-500">Période</Label>
            <Input className="h-9" placeholder="Ex. Mai 2026 / T2 2026"
              value={versementForm.periode}
              onChange={e => setVersementForm(f => ({ ...f, periode: e.target.value }))}
              data-testid="versement-periode" />
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">Date de versement *</Label>
            <Input className="h-9" type="date" value={versementForm.date_versement}
              onChange={e => setVersementForm(f => ({ ...f, date_versement: e.target.value }))}
              data-testid="versement-date" required />
          </div>
          <div>
            <Label className="text-[11px] text-neutral-500">Montant réel (€) *</Label>
            <Input className="h-9" type="number" step="0.01" min="0" placeholder="0.00"
              value={versementForm.montant_reel}
              onChange={e => setVersementForm(f => ({ ...f, montant_reel: e.target.value }))}
              data-testid="versement-montant" required />
          </div>
          <Button type="submit" size="sm" disabled={savingVersement} data-testid="versement-submit-btn">
            <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter
          </Button>
        </form>

        {versements.length === 0 ? (
          <p className="text-[12px] text-neutral-400 text-center py-6" data-testid="versements-empty">
            Aucun versement enregistré
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Période</TableHead>
                <TableHead>Date versement</TableHead>
                <TableHead className="text-right">Estimé (€)</TableHead>
                <TableHead className="text-right">Réel versé (€)</TableHead>
                <TableHead className="text-right">Écart</TableHead>
                <TableHead className="text-right">Écart %</TableHead>
                {isAdmin && <TableHead className="w-8"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {versements.map(v => {
                const positif = (v.ecart_eur || 0) >= 0;
                return (
                  <TableRow key={v.id} data-testid={`versement-row-${v.id}`}>
                    <TableCell className="font-medium">{v.periode}</TableCell>
                    <TableCell>{fmtDate(v.date_versement)}</TableCell>
                    <TableCell className="text-right text-neutral-500">{fmtEUR2(v.estime_a_date)}</TableCell>
                    <TableCell className="text-right font-medium">{fmtEUR2(v.montant_reel)}</TableCell>
                    <TableCell className={`text-right font-medium ${positif ? 'text-emerald-600' : 'text-red-600'}`}>
                      <span className="inline-flex items-center gap-0.5">
                        {positif ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
                        {positif ? '+' : ''}{fmtEUR2(v.ecart_eur)}
                      </span>
                    </TableCell>
                    <TableCell className={`text-right font-medium ${positif ? 'text-emerald-600' : 'text-red-600'}`}>
                      {positif ? '+' : ''}{Number(v.ecart_pct || 0).toFixed(2)}%
                    </TableCell>
                    {isAdmin && (
                      <TableCell>
                        <button onClick={() => deleteVersement(v.id)}
                          className="p-1 rounded hover:bg-red-50 text-neutral-300 hover:text-red-500"
                          data-testid={`versement-delete-${v.id}`} aria-label="Supprimer">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>

      {/* Hypothèses site */}
      {cfg.prerequisites?._hypotheses_site && (
        <Card className="border-neutral-200/80 bg-neutral-50/40" data-testid="hypotheses-site-card">
          <CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-widest text-neutral-500 font-semibold flex items-center gap-2 mb-3">
              <Info className="w-3.5 h-3.5" /> Hypothèses site
            </p>
            <div className="grid grid-cols-2 md:grid-cols-6 gap-3 text-[12px]">
              {[
                { label: 'Puissance souscrite', val: `${cfg.prerequisites._hypotheses_site.puissance_souscrite_kva} kVA` },
                { label: 'Conso annuelle', val: `${cfg.prerequisites._hypotheses_site.consommation_annuelle_mwh} MWh` },
                { label: 'Batterie', val: `${cfg.prerequisites._hypotheses_site.batterie_kwh} kWh` },
                { label: 'Puissance batterie', val: `${cfg.prerequisites._hypotheses_site.batterie_kw} kW` },
                { label: 'PV', val: `${cfg.prerequisites._hypotheses_site.pv_kwc} kWc` },
                { label: 'Contrat OA', val: `${cfg.prerequisites._hypotheses_site.contrat_oa_prix_kwh} €/kWh` },
              ].map((h, i) => (
                <div key={i}>
                  <p className="text-neutral-500 text-[10.5px] uppercase tracking-wide">{h.label}</p>
                  <p className="font-semibold text-neutral-900">{h.val}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Réglages avancés — édition du barème par mécanisme */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 rounded-md border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 transition-colors text-sm text-neutral-700 font-medium"
        onClick={() => setShowAdvanced((s) => !s)}
        data-testid="survoltage-advanced-toggle"
      >
        <span className="flex items-center gap-2">
          <ScaleIcon className="w-4 h-4" />
          Barème détaillé par mécanisme — fourchettes estimées (édition avancée)
        </span>
        {showAdvanced ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
      </button>

      {showAdvanced && (
        <Card data-testid="survoltage-advanced-panel">
          <CardContent className="p-5">
            <p className="text-[11.5px] text-neutral-500 mb-4">
              Cliquez sur un profil pour éditer ses valeurs bas/haut par mécanisme (€/an/client).
            </p>
            <div className="space-y-2">
              {Object.entries(cfg.profiles).map(([pkey, p]) => {
                const node = byProfile[pkey] || {};
                const isOpen = expandedProfile === pkey;
                return (
                  <div key={pkey} className="border border-neutral-200 rounded-md overflow-hidden">
                    <button
                      className="w-full flex items-center justify-between px-4 py-3 hover:bg-neutral-50 text-left"
                      onClick={() => setExpandedProfile(isOpen ? null : pkey)}
                      data-testid={`mech-toggle-${pkey}`}
                    >
                      <span className="text-[13px] font-medium text-neutral-800">{p.label}</span>
                      <span className="flex items-center gap-3 text-[11.5px] text-neutral-500">
                        <span>{fmtEUR(node.unit_low)} – {fmtEUR(node.unit_high)}</span>
                        {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                      </span>
                    </button>
                    {isOpen && (
                      <div className="px-4 py-3 bg-neutral-50/60 border-t border-neutral-200">
                        {(cfg.prerequisites?.[pkey] || []).length > 0 && (
                          <div className="mb-3 p-2.5 rounded-md bg-white border border-neutral-200" data-testid={`prerequisites-${pkey}`}>
                            <p className="text-[10.5px] uppercase tracking-wide text-neutral-500 font-semibold mb-1.5 flex items-center gap-1.5">
                              <ListChecks className="w-3 h-3" /> Prérequis techniques
                            </p>
                            <ul className="space-y-1">
                              {cfg.prerequisites[pkey].map((req, i) => (
                                <li key={i} className="text-[11.5px] text-neutral-700 flex items-start gap-1.5">
                                  <span className="w-1 h-1 rounded-full bg-neutral-400 mt-1.5 shrink-0" />
                                  <span>{req}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-xs">
                          {MECHANISMS.map(({ key, label }) => (
                            <div key={key} className="flex items-center gap-2" data-testid={`mech-row-${pkey}-${key}`}>
                              <span className="w-24 text-neutral-600 shrink-0">{label}</span>
                              <Input
                                type="number" min="0" step="0.1"
                                value={p[key]?.low ?? 0}
                                onChange={(e) => updateMech(pkey, key, 'low', e.target.value)}
                                className="w-20 h-8 text-right text-[12px]"
                                disabled={!isAdmin}
                                data-testid={`mech-input-${pkey}-${key}-low`}
                              />
                              <span className="text-neutral-400">–</span>
                              <Input
                                type="number" min="0" step="0.1"
                                value={p[key]?.high ?? 0}
                                onChange={(e) => updateMech(pkey, key, 'high', e.target.value)}
                                className="w-20 h-8 text-right text-[12px]"
                                disabled={!isAdmin}
                                data-testid={`mech-input-${pkey}-${key}-high`}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <MentionEstimation source="survoltage" bloc="survoltage-bareme"
                               className="mt-4" />
          </CardContent>
        </Card>
      )}

      {/* Gestion des documents Survoltage */}
      <AggregatorDocuments aggregator="survoltage" colorClass="text-violet-600" accentBg="bg-violet-50" />
    </div>
  );
}

function KPICard({ label, value, sub, accent, icon: Icon, tid }) {
  return (
    <div className="p-4 rounded-lg border border-neutral-200/80 bg-white" data-testid={tid}>
      <div className="flex items-center gap-2 mb-1">
        {Icon && <Icon className="w-3.5 h-3.5 text-neutral-400" />}
        <p className="text-[10px] uppercase tracking-wider text-neutral-400 font-medium">{label}</p>
      </div>
      <p className={`text-2xl font-semibold tracking-tight ${accent || 'text-neutral-900'}`}>{value}</p>
      {sub && <p className="text-[11px] text-neutral-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function Row({ l, v, strong }) {
  return (
    <div className="flex items-baseline justify-between">
      <span className="text-neutral-500">{l}</span>
      <span className={strong ? 'font-semibold text-neutral-900' : 'text-neutral-700 font-medium'}>{v}</span>
    </div>
  );
}
